import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { hasPermission } from "@/lib/permissions";
import type { PermissionKey, PermissionMap } from "@/lib/permissions";

interface TeamPermissionRow {
  profile_id: string;
  permission_key: string;
  enabled: boolean;
}

/**
 * Read all team_member_permissions rows for one profile.
 * Returns a map { [permission_key]: enabled }. Missing keys default to true
 * at the call-site via hasPermission() — the map only contains rows that exist
 * in the DB.
 */
export function useTeamPermissionsForProfile(profileId: string | undefined) {
  return useQuery({
    queryKey: ["team-permissions", profileId],
    enabled: !!profileId,
    queryFn: async (): Promise<PermissionMap> => {
      const { data, error } = await supabase
        .from("team_member_permissions")
        .select("profile_id, permission_key, enabled")
        .eq("profile_id", profileId!);
      if (error) throw error;
      const rows = (data ?? []) as TeamPermissionRow[];
      const out: PermissionMap = {};
      for (const r of rows) {
        out[r.permission_key as PermissionKey] = r.enabled;
      }
      return out;
    },
  });
}

/**
 * Permission map for the currently-signed-in user. Used by client-side guards
 * to hide UI affordances they no longer have. Server-side RLS remains the
 * authoritative gate.
 */
export function useCurrentUserPermissions() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["current-user-permissions"],
    enabled: !!user?.id,
    queryFn: async (): Promise<PermissionMap> => {
      const { data, error } = await supabase
        .from("team_member_permissions")
        .select("profile_id, permission_key, enabled")
        .eq("profile_id", user!.id);
      if (error) throw error;
      const rows = (data ?? []) as TeamPermissionRow[];
      const out: PermissionMap = {};
      for (const r of rows) {
        out[r.permission_key as PermissionKey] = r.enabled;
      }
      return out;
    },
  });
}

interface SetPermissionArgs {
  profile_id: string;
  permission_key: PermissionKey;
  enabled: boolean;
}

/**
 * Upsert one (profile_id, permission_key, enabled). Tracks who flipped it
 * via granted_by so we have an audit trail.
 */
export function useSetTeamPermission() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async ({ profile_id, permission_key, enabled }: SetPermissionArgs) => {
      const { error } = await supabase
        .from("team_member_permissions")
        .upsert(
          {
            profile_id,
            permission_key,
            enabled,
            granted_by: user?.id ?? null,
            updated_at: new Date().toISOString(),
          },
          { onConflict: "profile_id,permission_key" },
        );
      if (error) throw error;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["team-permissions", vars.profile_id] });
      qc.invalidateQueries({ queryKey: ["current-user-permissions"] });
    },
  });
}

export { hasPermission };
export type { PermissionKey, PermissionMap };

/**
 * Convenience hook for a single permission gate at a call site. Returns true
 * while the underlying query is loading so the UI never blocks on a slow
 * fetch — the server RLS layer remains the real gate.
 */
export function usePermissionGate(key: PermissionKey): boolean {
  const perms = useCurrentUserPermissions();
  if (perms.isLoading) return true;
  return hasPermission(perms.data, key);
}
